import { useLingui } from '@lingui/react/macro';
import { Menu } from 'lucide-react';

import OrganizerContainer from 'modules/organizer/ui/components/organizer-container';
import SubscriptionExpiryNotifier from 'modules/organizer/ui/components/subscription-expiry-notifier';
import JivoChatLoader from 'modules/support-chat/components/jivo-chat-loader';
import React from 'react';
import { Outlet } from 'react-router-dom';
import AiChatButton from 'modules/app-shell/ui/components/ai-chat-button';
import UDMenu from 'modules/app-shell/ui/components/menu';
import { useAnalyticsIdentity } from 'shared/analytics';
import { SkeletonPreviewSwitch } from 'ud-ui/components/skeleton';
import { useFeatureFlagQueryParam } from 'shared/feature-flags';
import { useMainLayout } from './context';
import * as S from './styles';

interface Props {
  children?: React.ReactNode;
  headerContent?: React.ReactNode;
  contentClassName?: string;
}

const UDMainLayout = React.memo((props: Props) => {
  const { headerContent, contentClassName } = useMainLayout();
  const [isMobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { t } = useLingui();

  // Параметр `?flow=v2` / `?flow=v1` в любом адресе под шеллом переключает флоу и стирается из адреса.
  useFeatureFlagQueryParam();

  // В шелле, а не в thunk-е входа: идентичность собирается из двух слайсов (`auth` и `organizer`)
  // и должна восстанавливаться при F5. Шелл — единственное место, которое видит оба и живёт всю
  // сессию; при выходе он размонтируется, и cleanup хука сбрасывает идентификацию.
  useAnalyticsIdentity();

  const openMenu = React.useCallback(() => setMobileMenuOpen(true), []);
  const closeMenu = React.useCallback(() => setMobileMenuOpen(false), []);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    if (isMobileMenuOpen) {
      document.addEventListener('keydown', onKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen, closeMenu]);

  return (
    <OrganizerContainer>
      {(isLoading, info) => (
        <S.Container>
          <S.Menu>
            <UDMenu isOrganizerLoading={isLoading} organizerInfo={info} />
          </S.Menu>
          <S.Header>
            <S.BurgerButton aria-label={t`Открыть меню`} onClick={openMenu} className="mr-2">
              <Menu />
            </S.BurgerButton>
            {headerContent || props.headerContent}
            <div className="ml-auto pl-2 md:pl-4">
              <AiChatButton />
            </div>
          </S.Header>

          {isMobileMenuOpen && (
            <>
              <S.MobileMenuOverlay onClick={closeMenu} />
              <S.MobileMenuPanel>
                <UDMenu isOrganizerLoading={isLoading} organizerInfo={info} />
              </S.MobileMenuPanel>
            </>
          )}

          <S.MainBlockWrapper>
            <S.Content className={contentClassName || props.contentClassName}>
              <JivoChatLoader />
              <SubscriptionExpiryNotifier />
              <Outlet />
            </S.Content>
            {/* Только при `npm start` и только на экране с `SkeletonPreview`. */}
            <SkeletonPreviewSwitch />
          </S.MainBlockWrapper>
        </S.Container>
      )}
    </OrganizerContainer>
  );
});

export default UDMainLayout;
